import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "is-flex-direction-column"
}
const _hoisted_2 = { class: "columns is-vcentered" }
const _hoisted_3 = { class: "column is-8" }
const _hoisted_4 = {
  id: "page-title",
  class: "title is-4 is-uppercase has-text-weight-medium is-inline"
}
const _hoisted_5 = {
  id: "litigations-count",
  class: "subtitle is-5 is-italic"
}
const _hoisted_6 = { class: "column is-4" }
const _hoisted_7 = { class: "icon" }
const _hoisted_8 = { class: "icon" }
const _hoisted_9 = { class: "el-dropdown-link" }
const _hoisted_10 = { class: "icon" }
const _hoisted_11 = ["href"]
const _hoisted_12 = ["href"]
const _hoisted_13 = ["href"]
const _hoisted_14 = { key: 0 }
const _hoisted_15 = {
  key: 0,
  class: "has-text-danger has-text-weight-bold"
}
const _hoisted_16 = {
  key: 1,
  class: "has-text-grey-light"
}
const _hoisted_17 = { class: "columns is-centered mt-5" }

import { Delete, Edit, View, WarningFilled } from '@element-plus/icons-vue';
import { debounce } from 'debounce';
import { ElMessage, ElMessageBox } from 'element-plus';
import { computed, onMounted, Ref, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { allowedAnalysisSortProps, Analysis, AnalysisSort, AnalysisStatus } from '@/Analysis/analysis.entity';
import AnalysisService from '@/Analysis/analysis.service';
import AnalysisStatusBadge from '@/components/AnalysisStatusBadge.vue';
import BulkApproveModal from '@/components/BulkApproveModal.vue';
import DecisionButtonAdd from '@/components/DecisionButtonAdd.vue';
import DuplicateDecisionModal from '@/components/DuplicateDecisionModal.vue';
import MemberAvatar from '@/components/MemberAvatar.vue';
import config from '@/config';
import { Entry } from '@/Entry/entry.entity';
import { formatDateTime } from '@/helpers/formatter';
import {
    canBulkApprove,
    canDeleteAnalysis,
    canEditAnalysis,
    canEditLitigation,
    canViewAnalysis,
} from '@/helpers/permission';
import { Litigation } from '@/Litigation/litigation.entity';
import LitigationService from '@/Litigation/litigation.service';
import { Member } from '@/Member/member.entity';
import MemberService from '@/Member/member.service';
import { useAnalysisStore } from '@/stores/analysis';
import { useSimilarAnalysesStore } from '@/stores/similar-analyses.store';

type AnalysisItem = {
    analysisId: string;
    litigation: Litigation;
    fileName: string;
    similarAnalysisCount: number;
    foremostEntries: Entry[];
    owner: Member;
    status: string;
    createdAt: string;
    updatedAt: string;
    errorCount: number;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'AnalysesList',
  props: {
    litigationId: {
        type: String,
        required: true,
    },
},
  setup(__props) {

const props = __props;



const { litigationId } = toRefs(props);

const { t } = useI18n();
const router = useRouter();
const analysisStore = useAnalysisStore();
const similarAnalysesStore = useSimilarAnalysesStore();

const apiUrl = config.apiUrl;
const litigation: Ref<Litigation | null> = ref(null);
const analyses: Ref<AnalysisItem[]> = ref([]);
const members: Ref<Member[]> = ref([]);
const currentPage = ref(1);
const filters: Ref<Record<string, string[]>> = ref({});
const defaultSort: AnalysisSort = { prop: 'updatedAt', order: 'descending' };
const sort = ref<AnalysisSort>({ ...defaultSort });
const total = ref(0);
const queryString = ref('');
const showBulkApproveModal = ref(false);

const analysisService = new AnalysisService();

const isTableReady = computed((): boolean => {
    return members.value.length > 0;
});

const persistSort = (nextSort: AnalysisSort): void => {
    if (litigation.value !== null) {
        analysisStore.setSort(litigation.value.name, nextSort.prop, nextSort.order);
    }
};

const getAnalyses = async (
    page: number,
    queryString: string,
    filters: Record<string, string[]>,
    sortQuery: string,
): Promise<void> => {
    const response = await analysisService.findAll(
        page,
        queryString,
        {
            ...filters,
            ...{ litigation: [litigationId.value] },
        },
        sortQuery,
    );
    total.value = Number(response.meta.count);
    analyses.value = response.data.map((analysis: Analysis) => ({
        analysisId: analysis.analysisId,
        litigation: analysis.litigation,
        fileName: analysis.decision?.fileName || '',
        similarAnalysisCount: analysis.decision?.similarAnalysisCount ?? 1,
        foremostEntries: analysis.foremostEntries,
        owner: analysis.owner,
        status: analysis.status,
        createdAt: analysis.createdAt,
        updatedAt: analysis.updatedAt,
        errorCount: analysis.errorCount ?? 0,
    }));
};

const refreshAnalyses = async (): Promise<void> => {
    if (!allowedAnalysisSortProps.includes(sort.value.prop)) {
        sort.value = { ...defaultSort };
        persistSort(defaultSort);
    }

    const sortQuery = sort.value.order === 'descending' ? `-${sort.value.prop}` : sort.value.prop;

    try {
        await getAnalyses(currentPage.value, queryString.value, filters.value, sortQuery);
    } catch (error) {
        const { message } = error as { message: string };
        ElMessage.error(message);
    }
};

const debounceRefreshAnalyses = debounce(function debounceRefreshAnalyses(this: any) {
    refreshAnalyses();
}, 1000);

const getLitigation = async (): Promise<void> => {
    const litigationService = new LitigationService();
    try {
        litigation.value = await litigationService.find(litigationId.value);
    } catch (error) {
        const { message } = error as { message: string };
        ElMessage.error(message);
    }
};

const getMembers = async (): Promise<void> => {
    const memberService = new MemberService();
    try {
        members.value = await memberService.findAll();
    } catch (error) {
        const { message } = error as { message: string };
        ElMessage.error(message);
    }
};

const deleteAnalysis = async (analysisId: string): Promise<void> => {
    try {
        await ElMessageBox.confirm(t('analysis.deleteConfirmMessage'), t('analysis.deleteConfirmTitle'), {
            type: 'warning',
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        });
        try {
            await similarAnalysesStore.deleteAnalysis(analysisId);
            ElMessage.success(t('analysis.deleteConfirmed'));
            analyses.value = analyses.value.filter((currentAnalysis) => currentAnalysis.analysisId !== analysisId);
        } catch (error) {
            const { message } = error as { message: string };
            ElMessage.error(message);
        }
    } catch {
        ElMessage.info(t('analysis.deleteCanceled'));
    }
};

const openSimilarModal = async (row: AnalysisItem): Promise<void> => {
    try {
        await similarAnalysesStore.checkAnalysisIdForSimilarAnalyses(row.analysisId, row.fileName);
    } catch (error) {
        const { message } = error as { message: string };
        ElMessage.error(message);
    }
};

const getOwnerFilterValues = () => {
    return members.value
        .sort((a, b) => {
            if (`${a.firstName} ${a.lastName}` < `${b.firstName} ${b.lastName}`) {
                return -1;
            }
            if (`${a.firstName} ${a.lastName}` > `${b.firstName} ${b.lastName}`) {
                return 1;
            }
            return 0;
        })
        .map((member) => ({
            text: `${member.firstName} ${member.lastName}`,
            value: member.memberId,
        }));
};

const getStatusFilterValues = () => {
    return Object.values(AnalysisStatus).map((status) => {
        return { text: t(`status.${status}`), value: status };
    });
};

const changePage = async (): Promise<void> => {
    await refreshAnalyses();
};

const changeSort = async ({ prop, order }: { prop: string; order: 'ascending' | 'descending' }): Promise<void> => {
    sort.value = { prop, order };
    persistSort(sort.value);

    await refreshAnalyses();
};

const changeFilter = async (newFilters: Record<string, string[]>): Promise<void> => {
    if (litigation.value !== null) {
        analysisStore.addFilter(litigation.value.name, newFilters);
    }
    filters.value = { ...filters.value, ...newFilters };
    currentPage.value = 1;
    await refreshAnalyses();
};

const cellClassName = ({ column }: { column: { property: string } }): string => {
    if (column.property === 'status') {
        return 'analysis-status';
    }

    return '';
};

const recentAnalysis = ({ row }: { row: Analysis }) => {
    if (analysisStore.isRecent(row.analysisId)) {
        return 'recent-row';
    }
};

onMounted(async () => {
    similarAnalysesStore.closeDuplicateDecisionModal();

    await getLitigation();

    if (litigation.value !== null) {
        const savedSort = analysisStore.getSort(litigation.value.name);
        sort.value = savedSort !== null ? savedSort : { ...defaultSort };

        const savedFilters = analysisStore.getFilters(litigation.value.name);
        // 'createdAt' filter was removed when the column was replaced with 'updatedAt'; clear any stale persisted value.
        delete savedFilters.createdAt;
        filters.value = savedFilters;
    }

    getMembers();
    refreshAnalyses();
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_router_link = _resolveComponent("router-link")!
  const _component_el_dropdown_item = _resolveComponent("el-dropdown-item")!
  const _component_el_dropdown_menu = _resolveComponent("el-dropdown-menu")!
  const _component_el_dropdown = _resolveComponent("el-dropdown")!
  const _component_el_space = _resolveComponent("el-space")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_tooltip = _resolveComponent("el-tooltip")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_link = _resolveComponent("el-link")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_empty = _resolveComponent("el-empty")!

  return (litigation.value != null)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("p", _hoisted_4, _toDisplayString(litigation.value.name) + " / " + _toDisplayString(_ctx.$t('listAnalyses.analyses')), 1),
              _createElementVNode("span", _hoisted_5, " (" + _toDisplayString(total.value) + ")", 1)
            ]),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_component_el_space, { class: "is-pulled-right" }, {
                default: _withCtx(() => [
                  (_unref(canEditLitigation)(litigation.value))
                    ? (_openBlock(), _createBlock(_component_router_link, {
                        key: 0,
                        id: "litigation-edit",
                        to: { name: 'litigation-form', params: { litigationId: litigation.value.litigationId } },
                        class: "button is-primary is-radius-small has-text-weight-semibold",
                        exact: ""
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_7, [
                            _createVNode(_component_font_awesome_icon, { icon: "cogs" })
                          ]),
                          _createElementVNode("span", null, _toDisplayString(_ctx.$t('common.edit')), 1)
                        ]),
                        _: 1
                      }, 8, ["to"]))
                    : _createCommentVNode("", true),
                  (_unref(canBulkApprove)(litigation.value))
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        id: "bulk-approve",
                        class: "button is-primary is-radius-small has-text-weight-semibold",
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (showBulkApproveModal.value = true))
                      }, [
                        _createElementVNode("span", _hoisted_8, [
                          _createVNode(_component_font_awesome_icon, { icon: "check" })
                        ]),
                        _createElementVNode("span", null, _toDisplayString(_ctx.$t('bulkApprove.approve')), 1)
                      ]))
                    : _createCommentVNode("", true),
                  _createVNode(_component_el_dropdown, { class: "button is-primary is-radius-small has-text-weight-semibold" }, {
                    dropdown: _withCtx(() => [
                      _createVNode(_component_el_dropdown_menu, null, {
                        default: _withCtx(() => [
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['xlsx', 'csv'], (extension) => {
                            return _createVNode(_component_el_dropdown_item, { key: extension }, {
                              default: _withCtx(() => [
                                _createElementVNode("a", {
                                  href: `${_unref(apiUrl)}/exports/analyses.${extension}?litigation=${litigation.value.name}`
                                }, _toDisplayString(_ctx.$t('listAnalyses.exportAnalyses')) + " (. " + _toDisplayString(extension.toUpperCase()) + ")", 9, _hoisted_11)
                              ]),
                              _: 2
                            }, 1024)
                          }), 64)),
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['xlsx', 'csv'], (extension) => {
                            return _createVNode(_component_el_dropdown_item, { key: extension }, {
                              default: _withCtx(() => [
                                _createElementVNode("a", {
                                  href: `${_unref(apiUrl)}/exports/centralization.${extension}?litigation=${litigation.value.name}`
                                }, _toDisplayString(_ctx.$t('listAnalyses.exportCentralization')) + " (. " + _toDisplayString(extension.toUpperCase()) + ")", 9, _hoisted_12)
                              ]),
                              _: 2
                            }, 1024)
                          }), 64)),
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['xlsx', 'csv'], (extension) => {
                            return _createVNode(_component_el_dropdown_item, { key: extension }, {
                              default: _withCtx(() => [
                                _createElementVNode("a", {
                                  href: `${_unref(apiUrl)}/exports/monotony.${extension}?litigation=${litigation.value.name}`
                                }, _toDisplayString(_ctx.$t('listAnalyses.exportMonotony')) + " (. " + _toDisplayString(extension.toUpperCase()) + ")", 9, _hoisted_13)
                              ]),
                              _: 2
                            }, 1024)
                          }), 64))
                        ]),
                        _: 1
                      })
                    ]),
                    default: _withCtx(() => [
                      _createElementVNode("span", _hoisted_9, [
                        _createElementVNode("span", _hoisted_10, [
                          _createVNode(_component_font_awesome_icon, { icon: "download" })
                        ]),
                        _createElementVNode("span", null, _toDisplayString(_ctx.$t('listAnalyses.export')), 1)
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(DecisionButtonAdd, {
                    litigation: litigation.value,
                    onSuccess: refreshAnalyses
                  }, null, 8, ["litigation"])
                ]),
                _: 1
              })
            ])
          ]),
          (analyses.value.length > 0 || queryString.value !== '' || Object.keys(filters.value).length > 0)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (isTableReady.value)
                  ? (_openBlock(), _createBlock(_component_el_table, {
                      key: 0,
                      data: analyses.value,
                      "cell-class-name": cellClassName,
                      "default-sort": sort.value,
                      "row-class-name": recentAnalysis,
                      onSortChange: changeSort,
                      onFilterChange: changeFilter
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_table_column, {
                          width: "45",
                          align: "center"
                        }, {
                          default: _withCtx((scope) => [
                            (scope.row.similarAnalysisCount > 1)
                              ? (_openBlock(), _createBlock(_component_el_tooltip, {
                                  key: 0,
                                  content: _ctx.$t('listAnalyses.multipleAnalysesTooltip'),
                                  placement: "top"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_el_icon, {
                                      color: "var(--el-color-warning)",
                                      style: {"cursor":"pointer"},
                                      size: "large",
                                      onClick: ($event: any) => (openSimilarModal(scope.row))
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(WarningFilled))
                                      ]),
                                      _: 2
                                    }, 1032, ["onClick"])
                                  ]),
                                  _: 2
                                }, 1032, ["content"]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, {
                          prop: "fileName",
                          "column-key": "fileName",
                          label: _ctx.$t('listAnalyses.fileName')
                        }, null, 8, ["label"]),
                        _createVNode(_component_el_table_column, {
                          prop: "foremostCriteria",
                          label: _ctx.$t('listAnalyses.foremostCriteria')
                        }, {
                          default: _withCtx((scope) => [
                            (scope.row.foremostEntries.length)
                              ? (_openBlock(), _createElementBlock("ul", _hoisted_14, [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scope.row.foremostEntries, (foremostEntry) => {
                                    return (_openBlock(), _createElementBlock("li", {
                                      key: foremostEntry.entryId
                                    }, _toDisplayString(foremostEntry.criterion.title) + " : " + _toDisplayString(foremostEntry.value.join(';')), 1))
                                  }), 128))
                                ]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        }, 8, ["label"]),
                        _createVNode(_component_el_table_column, {
                          prop: "owner",
                          "column-key": "owner",
                          align: "center",
                          label: _ctx.$t('listAnalyses.analyzedBy'),
                          filters: getOwnerFilterValues(),
                          "filtered-value": filters.value.owner
                        }, {
                          default: _withCtx((scope) => [
                            _createVNode(MemberAvatar, {
                              member: scope.row.owner
                            }, null, 8, ["member"])
                          ]),
                          _: 1
                        }, 8, ["label", "filters", "filtered-value"]),
                        _createVNode(_component_el_table_column, {
                          align: "center",
                          prop: "status",
                          "column-key": "status",
                          label: _ctx.$t('listAnalyses.status'),
                          sortable: "custom",
                          filters: getStatusFilterValues(),
                          "filtered-value": filters.value.status
                        }, {
                          default: _withCtx((scope) => [
                            _createVNode(AnalysisStatusBadge, {
                              status: scope.row.status
                            }, null, 8, ["status"])
                          ]),
                          _: 1
                        }, 8, ["label", "filters", "filtered-value"]),
                        _createVNode(_component_el_table_column, {
                          prop: "errorCount",
                          "column-key": "errorCount",
                          align: "center",
                          width: "120",
                          label: _ctx.$t('listAnalyses.errorCount')
                        }, {
                          default: _withCtx((scope) => [
                            (scope.row.errorCount > 0)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(scope.row.errorCount), 1))
                              : (_openBlock(), _createElementBlock("span", _hoisted_16, "—"))
                          ]),
                          _: 1
                        }, 8, ["label"]),
                        _createVNode(_component_el_table_column, {
                          prop: "updatedAt",
                          "column-key": "updatedAt",
                          label: _ctx.$t('listAnalyses.lastModifiedDate'),
                          sortable: "custom"
                        }, {
                          default: _withCtx((scope) => [
                            _createTextVNode(_toDisplayString(_unref(formatDateTime)(scope.row.updatedAt)), 1)
                          ]),
                          _: 1
                        }, 8, ["label"]),
                        _createVNode(_component_el_table_column, null, {
                          header: _withCtx(() => [
                            _createVNode(_component_el_input, {
                              modelValue: queryString.value,
                              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryString).value = $event)),
                              placeholder: _ctx.$t('listAnalyses.search'),
                              clearable: "",
                              onInput: _unref(debounceRefreshAnalyses)
                            }, null, 8, ["modelValue", "placeholder", "onInput"])
                          ]),
                          default: _withCtx((scope) => [
                            (_unref(canEditAnalysis)(scope.row))
                              ? (_openBlock(), _createBlock(_component_el_link, {
                                  key: 0,
                                  icon: _unref(Edit),
                                  underline: false,
                                  href: 
                                    _unref(router).resolve({
                                        name: 'analysis-form',
                                        params: { analysisId: scope.row.analysisId },
                                    }).href
                                
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode("  " + _toDisplayString(_ctx.$t('common.edit')), 1)
                                  ]),
                                  _: 2
                                }, 1032, ["icon", "href"]))
                              : (_unref(canViewAnalysis)(scope.row))
                                ? (_openBlock(), _createBlock(_component_el_link, {
                                    key: 1,
                                    icon: _unref(View),
                                    underline: false,
                                    href: 
                                    _unref(router).resolve({
                                        name: 'analysis-form',
                                        params: { analysisId: scope.row.analysisId },
                                    }).href
                                
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode("  " + _toDisplayString(_ctx.$t('common.view')), 1)
                                    ]),
                                    _: 2
                                  }, 1032, ["icon", "href"]))
                                : _createCommentVNode("", true),
                            _createTextVNode("   "),
                            (_unref(canDeleteAnalysis)(scope.row))
                              ? (_openBlock(), _createBlock(_component_el_button, {
                                  key: 2,
                                  circle: "",
                                  plain: "",
                                  icon: _unref(Delete),
                                  onClick: ($event: any) => (deleteAnalysis(scope.row.analysisId))
                                }, null, 8, ["icon", "onClick"]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["data", "default-sort"]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_17, [
                  _createVNode(_component_el_pagination, {
                    currentPage: currentPage.value,
                    "onUpdate:currentPage": _cache[2] || (_cache[2] = ($event: any) => ((currentPage).value = $event)),
                    background: "",
                    layout: "prev, pager, next",
                    total: total.value,
                    "page-size": 20,
                    onCurrentChange: _cache[3] || (_cache[3] = ($event: any) => (changePage()))
                  }, null, 8, ["currentPage", "total"])
                ])
              ], 64))
            : (_openBlock(), _createBlock(_component_el_empty, { key: 1 }))
        ]),
        (showBulkApproveModal.value)
          ? (_openBlock(), _createBlock(BulkApproveModal, {
              key: 0,
              "litigation-id": _unref(litigationId),
              onApproved: refreshAnalyses,
              onClose: _cache[4] || (_cache[4] = ($event: any) => (showBulkApproveModal.value = false))
            }, null, 8, ["litigation-id"]))
          : _createCommentVNode("", true),
        (_unref(similarAnalysesStore).isDuplicateDecisionModalOpen)
          ? (_openBlock(), _createBlock(DuplicateDecisionModal, {
              key: 1,
              warnings: _unref(similarAnalysesStore).warnings,
              onDelete: _cache[5] || (_cache[5] = ($event: any) => (refreshAnalyses())),
              onClose: _cache[6] || (_cache[6] = ($event: any) => (_unref(similarAnalysesStore).closeDuplicateDecisionModal()))
            }, null, 8, ["warnings"]))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})